import React from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import * as Bringg from '@bringg/types';
import { BringgInput, SelectableList } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { withErrorBoundary } from '@bringg-frontend/bringg-web-infra';

import VrpAutoDispatchAnalyticsContainer from 'bringg-web/features/vrp-autodispatch-analytics/vrp-auto-dispatch-analytics-container';
import EmployeeRow from './employee-row';
import EmployeesFilter from './employees-filter/employees-filter';
import { useHasFeatureFlag } from 'bringg-web/utils/feature-flags';
import { EmployeeStateFilter } from './types';
import { useCrossAppData } from './hooks/use-cross-app-data';
import { useStoreData } from './hooks/use-store-data';

import styles from './employee-list.module.scss';

export interface RegisterCallbacks {
	setSelectedEmployee(selectedEmployee: Bringg.User | null | undefined): void;

	setDisplayedEmployees(newDisplayed: DisplayedEmployees): void;

	setDriverListFilterByOnShift(fn: () => boolean): void;
	// Must be called after all above functions called
	finish(): void;
}

export interface DisplayedEmployees {
	all: Bringg.User[];
	filtered: Bringg.User[];
	onlineCount: number;
	onShiftAndOfflineCount: number;
	onShiftCount: number;
	offShiftCount: number;
}

const EmployeeList = ({ cameFromEmployeeInFocus }: { cameFromEmployeeInFocus?: boolean }) => {
	const { t } = useTranslation();

	const displayVRPADAnalytics = useHasFeatureFlag('display_vrp_auto_dispatch_analytics');

	const {
		initFinished,
		displayedEmployees,
		selectedEmployee,
		filterEmployees,
		changeEmployeesFilterType,
		changeEmployeesAvailabilityStateFilter,
		driverListFilterByOnShift
	} = useCrossAppData();
	const { userTypesReady, vehiclesAreLoading } = useStoreData();

	const employeeStateFilterTypeCounts: Record<EmployeeStateFilter, number> = {
		[EmployeeStateFilter.ALL]: displayedEmployees.all.length,
		[EmployeeStateFilter.ONLINE]: displayedEmployees.onlineCount,
		[EmployeeStateFilter.ON_SHIFT]: displayedEmployees.onShiftCount,
		[EmployeeStateFilter.ON_SHIFT_OFFLINE]: displayedEmployees.onShiftAndOfflineCount,
		[EmployeeStateFilter.OFF_SHIFT]: displayedEmployees.offShiftCount
	};

	if (!initFinished || !userTypesReady || vehiclesAreLoading) {
		return <div className={styles.employeeList} id="employee_list"></div>;
	}

	return (
		<div className={styles.employeeList} id="employee_list">
			{displayVRPADAnalytics ? <VrpAutoDispatchAnalyticsContainer /> : null}

			<div className={styles.employeesHeader}>
				<BringgInput
					placeholder={t('GLOBAL.SEARCH')}
					className={styles.searchDrivers}
					onChange={e => filterEmployees(e.target.value)}
					prefix={<BringgIcon className="search-icon" iconName={BringgFontIcons.Search} />}
				/>
				<EmployeesFilter
					cameFromEmployeeInFocus={cameFromEmployeeInFocus}
					onFilterUpdate={({ employeeState, tasksState }) => {
						changeEmployeesFilterType(employeeState);
						changeEmployeesAvailabilityStateFilter(tasksState);
					}}
					employeeStateFilterTypeCounts={employeeStateFilterTypeCounts}
				/>
			</div>

			{displayedEmployees.filtered.length ? (
				<SelectableList
					itemClassName={styles.employeeListItemWrapper}
					labelKey="name"
					renderValue={employee => (
						<EmployeeRow
							employee={employee}
							selectedEmployeeId={selectedEmployee?.id}
							driverListFilterByOnShift={driverListFilterByOnShift}
						/>
					)}
					items={displayedEmployees.filtered}
					// required
					onSelect={() => undefined}
				/>
			) : null}
		</div>
	);
};

export default withErrorBoundary(observer(EmployeeList));
